import { useEffect, useRef, useState } from 'react';

/**
 * A tela de carga sai desaparecendo, e não de um quadro para o outro.
 *
 * ── O que acontecia ───────────────────────────────────────────────────────
 *
 * `{dataLoading && <TelaDeCarga />}` — quando o dado chegava, o overlay
 * deixava de existir e o painel inteiro aparecia de uma vez: nove seções,
 * quatro cards e uma rosca colorida surgindo juntos. É o mesmo susto que uma
 * luz acesa de repente.
 *
 * ── Por que não dá para ser só um `&&` ───────────────────────────────────
 *
 * Para a tela poder desaparecer, ela precisa continuar MONTADA depois de o
 * carregamento acabar. O booleano sozinho a desmonta no mesmo instante, e não
 * existe elemento para animar. Então são dois estados: `visivel` (ainda no
 * DOM) e `saindo` (já com a opacidade indo a zero).
 *
 * ── Por que a SAÍDA e não a entrada ──────────────────────────────────────
 *
 * A entrada já teve animação e foi removida por causar piscada: no boot há
 * TRÊS renderizações da mesma tela — a estática do `index.html`, a do React
 * enquanto a sessão resolve, e a do React enquanto os dados vêm. As duas
 * últimas são componentes em posições diferentes da árvore, então uma desmonta
 * e a outra monta, e a animação de entrada rodava de novo em cada uma, por
 * cima de uma tela que já estava lá.
 *
 * Sair acontece UMA vez, no fim. É o único momento em que animar aqui não
 * repete.
 *
 * ── Voltar a carregar cancela a saída ────────────────────────────────────
 *
 * Se `carregando` virar `true` de novo no meio do desaparecimento — um
 * refresh disparado por outra parte da tela — a saída é abortada e a tela
 * volta a ficar cheia. Sem isso ela terminaria de sumir e reapareceria do
 * zero, que é justamente a piscada que se quer evitar.
 */

/** Precisa bater com a transição de `.app-loading-overlay.saindo` no App.css. */
export const DURACAO_DA_SAIDA_MS = 280;

export interface SaidaDaCarga {
  /** Ainda está no DOM. */
  visivel: boolean;
  /** Já está desaparecendo. */
  saindo: boolean;
}

export const useSaidaDaCarga = (carregando: boolean): SaidaDaCarga => {
  const [visivel, setVisivel] = useState(carregando);
  const [saindo, setSaindo] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (carregando) {
      if (timer.current) { clearTimeout(timer.current); timer.current = null; }
      setSaindo(false);
      setVisivel(true);
      return;
    }
    if (!visivel) return;

    setSaindo(true);
    timer.current = setTimeout(() => {
      setVisivel(false);
      setSaindo(false);
      timer.current = null;
    }, DURACAO_DA_SAIDA_MS);

    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [carregando, visivel]);

  return { visivel, saindo };
};
